import React, { useState } from 'react';
import { Search, ClipboardCheck, Workflow, Hammer } from 'lucide-react';
type TabId = 'audit' | 'meetings' | 'automation' | 'tools';
type Tab = {
  id: TabId;
  label: string;
  icon: React.ElementType;
  competitor: {
    name: string;
    monogram: string;
    brand: string;
    logo: string;
    text: string;
  };
  giga: {
    chip: string;
    text: string;
  };
};
const TABS: Tab[] = [{
  id: 'audit',
  label: 'Ad Spend Audit',
  icon: Search,
  competitor: {
    name: 'ChatGPT',
    monogram: 'AI',
    brand: '#10A37F',
    logo: 'openai',
    text: 'Tells you how to audit your ad spend.'
  },
  giga: {
    chip: 'Audits it.',
    text: 'Hands you the PDF.'
  }
}, {
  id: 'meetings',
  label: 'Meeting Follow-ups',
  icon: ClipboardCheck,
  competitor: {
    name: 'Copilot',
    monogram: 'C',
    brand: '#000000',
    logo: 'githubcopilot',
    text: 'Summarizes your meetings.'
  },
  giga: {
    chip: 'Creates the tasks,',
    text: 'sends the follow-ups, updates the CRM.'
  }
}, {
  id: 'automation',
  label: 'Workflow Automation',
  icon: Workflow,
  competitor: {
    name: 'Zapier',
    monogram: 'Z',
    brand: '#FF4A00',
    logo: 'zapier',
    text: 'Follows rules you write.'
  },
  giga: {
    chip: 'Figures out',
    text: 'what needs automating and does it.'
  }
}, {
  id: 'tools',
  label: 'Building Tools',
  icon: Hammer,
  competitor: {
    name: 'Claude Code',
    monogram: 'Cl',
    brand: '#D97757',
    logo: 'claude',
    text: 'Writes the code. You figure out the rest.'
  },
  giga: {
    chip: 'Builds it,',
    text: 'ships it, sends you the link.'
  }
}];
// Brand logo tile with graceful fallback to the monogram if the logo CDN fails.
function CompetitorLogo({
  competitor
}: {
  competitor: Tab['competitor'];
}) {
  const [failed, setFailed] = useState(false);
  return <span className="flex items-center justify-center w-6 h-6 rounded-md text-white font-bold text-[10px] tracking-tight shrink-0 overflow-hidden" style={{
    backgroundColor: competitor.brand
  }} aria-hidden="true" data-id="element-5176" data-mp="1w7azp6i5mxoy">
      {failed ? competitor.monogram : <img src={`https://cdn.simpleicons.org/${competitor.logo}/white`} alt="" aria-hidden="true" onError={() => setFailed(true)} className="w-3.5 h-3.5 object-contain" data-id="element-5177" data-mp="34nuvs52ikj" />}
    </span>;
}
function GigaMark() {
  return <span className="flex items-center justify-center w-6 h-6 rounded-md bg-[#EA580C] shrink-0" data-id="element-5178" data-mp="94kz15qs2opt">
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-5179" data-mp="e47bve2p22y3" />
    </span>;
}
export function ToolComparison() {
  // Hidden per user request, but code preserved for later.
  return null;
  return <section className="w-full bg-[#FAF8F4] py-12 md:py-32 border-t border-neutral-200/70 overflow-hidden" data-id="element-5180" data-mp="wufbhm76qb9j">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-5181" data-mp="11s73shbqrze9">
        {/* Intro */}
        <div className="text-left md:text-center mb-12 md:mb-16" data-id="element-5182" data-mp="1bwne6v6gcoao">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-5183" data-mp="1buu9hlfege5b">
            Giga vs AI tools
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-5184" data-mp="11zsi0ixnizzk">
            Built to do the work — not just talk about it.
          </h2>
          <p className="text-base md:text-xl text-neutral-500 leading-relaxed max-w-2xl md:mx-auto" data-id="element-5185" data-mp="1umddnvl5ty8x">
            Most AI stops at the suggestion. Giga plugs into your stack, takes
            the action, and hands back the finished result — ready when you are.
          </p>
        </div>

        {/* Comparison */}
        <div className="max-w-5xl mx-auto" data-id="element-5186" data-mp="1gvi92vhp23b5">
          {/* Column labels (desktop) */}
          <div className="hidden md:grid grid-cols-[1.1fr_1.3fr_1.6fr] gap-6 px-2 mb-5" data-id="element-5187" data-mp="2depo75lyw5u5">
            <span className="text-[11px] font-bold tracking-[1.5px] uppercase text-neutral-400" data-id="element-5188" data-mp="28m4ue4329sqm">
              Scenario
            </span>
            <span className="text-[11px] font-bold tracking-[1.5px] uppercase text-neutral-400" data-id="element-5189" data-mp="1t79b7vx0fatm">
              The answer
            </span>
            <span className="text-[11px] font-bold tracking-[1.5px] uppercase text-[#EA580C]" data-id="element-5190" data-mp="1k4zxkx6g31c0">
              The action
            </span>
          </div>

          {/* Rows */}
          <div className="flex flex-col gap-4 md:gap-3" data-id="element-5191" data-mp="2bvnyc847i4p5">
            {TABS.map(tab => <div key={tab.id} className="group rounded-2xl bg-white border border-neutral-200/70 shadow-[0_1px_2px_rgba(43,33,26,0.03)] transition-shadow duration-300 hover:shadow-[0_12px_40px_rgba(43,33,26,0.06)] overflow-hidden" data-id="element-5192" data-mp="2bxr01peq1m9p">
                <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1.3fr_1.6fr] md:items-stretch" data-id="element-5193" data-mp="274pif9n4r8mf">
                  {/* Scenario */}
                  <div className="flex items-center gap-3 px-6 py-5 md:py-7 border-b md:border-b-0 border-neutral-100" data-id="element-5194" data-mp="2utqjgbtmvhho">
                    <div className="w-10 h-10 rounded-xl bg-[#FAF8F4] flex items-center justify-center text-neutral-500 shrink-0 transition-colors group-hover:text-[#2B211A]" data-id="element-5195" data-mp="2zmjo6zp3qptr">
                      <tab.icon size={19} strokeWidth={1.75} data-id="element-5196" data-mp="j6ezm5eev5ui" />
                    </div>
                    <span className="font-bold text-[#2B211A] leading-tight" data-id="element-5197" data-mp="xv1ds3saxrk5">
                      {tab.label}
                    </span>
                  </div>

                  {/* The answer */}
                  <div className="px-6 py-5 md:py-7 border-b md:border-b-0 md:border-l border-neutral-100" data-id="element-5198" data-mp="58gjovkfz1cw">
                    <div className="flex items-center gap-2 mb-2" data-id="element-5199" data-mp="9m3809y5ovy">
                      <CompetitorLogo competitor={tab.competitor} data-id="element-5200" data-mp="f64ipxp2yv0v" />
                      <span className="font-semibold text-[#2B211A] text-sm" data-id="element-5201" data-mp="f38k075es2bd">
                        {tab.competitor.name}
                      </span>
                      <span className="ml-auto md:hidden text-[10px] font-bold tracking-[1.5px] uppercase text-neutral-300" data-id="element-5202" data-mp="1qjp4wfzqfb5m">
                        Answer
                      </span>
                    </div>
                    <p className="text-neutral-500 text-sm font-medium leading-relaxed" data-id="element-5203" data-mp="1so29tcblu0dt">
                      {tab.competitor.text}
                    </p>
                  </div>

                  {/* The action (Giga) — soft warm tint, not a heavy slab */}
                  <div className="px-6 py-5 md:py-7 bg-[#FFF6F0] md:border-l border-[#F6E2D4]" data-id="element-5204" data-mp="27luo615f0iqf">
                    <div className="flex items-center gap-2 mb-2" data-id="element-5205" data-mp="19ym5edwstns4">
                      <GigaMark data-id="element-5206" data-mp="19w9fneksu94i" />
                      <span className="font-semibold text-[#2B211A] text-sm" data-id="element-5207" data-mp="1ewxegmf9ru9a">
                        Giga
                      </span>
                      <span className="ml-auto md:hidden text-[10px] font-bold tracking-[1.5px] uppercase text-[#EA580C]" data-id="element-5208" data-mp="1jvryfroz3ouz">
                        Action
                      </span>
                    </div>
                    <p className="text-[#2B211A] text-sm font-medium leading-relaxed" data-id="element-5209" data-mp="r7csrvrqeiwb">
                      <span className="font-bold text-[#C2410C]" data-id="element-5210" data-mp="w5o285e46re0">
                        {tab.giga.chip}
                      </span>{' '}
                      {tab.giga.text}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}